import {
  Activity,
  Droplets,
  Pill,
  Syringe,
  Scale,
  Heart,
  UtensilsCrossed,
  Footprints,
  History,
} from 'lucide-react';

interface HealthScreenProps {
  onNavigate: (page: string) => void;
}

export function HealthScreen({ onNavigate }: HealthScreenProps) {
  const primaryTrackingOptions = [
    {
      id: 'glucose-tracker',
      title: 'Glucose Readings',
      icon: Droplets,
      color: 'blue',
      description: 'Track your blood sugar levels',
    },
    {
      id: 'calorie-tracker',
      title: 'Calorie Tracking',
      icon: UtensilsCrossed,
      color: 'orange',
      description: 'Track your food intake',
    },
    {
      id: 'steps-tracker',
      title: 'Steps Tracking',
      icon: Footprints,
      color: 'cyan',
      description: 'Monitor daily steps',
    },
    {
      id: 'steps-tracker',
      title: 'Activity Log',
      icon: Activity,
      color: 'green',
      description: 'Review workouts and activity history',
    },
  ];

  const additionalTrackingOptions = [
    {
      id: 'medication-tracker',
      title: 'Medications',
      icon: Pill,
      color: 'purple',
      description: 'Log your medications',
    },
    {
      id: 'insulin-tracker',
      title: 'Insulin',
      icon: Syringe,
      color: 'pink',
      description: 'Track insulin injections',
    },
    {
      id: 'weight-tracker',
      title: 'Weight Tracking',
      icon: Scale,
      color: 'green',
      description: 'Monitor your weight',
    },
    {
      id: 'blood-pressure-tracker',
      title: 'Blood Pressure',
      icon: Heart,
      color: 'red',
      description: 'Record BP readings',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; hover: string; icon: string; border: string }> = {
      blue: {
        bg: 'bg-dropsugar-green/10',
        hover: 'hover:bg-dropsugar-green/20',
        icon: 'text-dropsugar-green',
        border: 'border-dropsugar-green',
      },
      purple: {
        bg: 'bg-purple-50',
        hover: 'hover:bg-purple-100',
        icon: 'text-purple-600',
        border: 'border-purple-500',
      },
      pink: {
        bg: 'bg-pink-50',
        hover: 'hover:bg-pink-100',
        icon: 'text-pink-600',
        border: 'border-pink-500',
      },
      green: {
        bg: 'bg-green-50',
        hover: 'hover:bg-green-100',
        icon: 'text-green-600',
        border: 'border-green-500',
      },
      red: {
        bg: 'bg-red-50',
        hover: 'hover:bg-red-100',
        icon: 'text-red-600',
        border: 'border-red-500',
      },
      orange: {
        bg: 'bg-orange-50',
        hover: 'hover:bg-orange-100',
        icon: 'text-orange-600',
        border: 'border-orange-500',
      },
      cyan: {
        bg: 'bg-cyan-50',
        hover: 'hover:bg-cyan-100',
        icon: 'text-cyan-600',
        border: 'border-cyan-500',
      },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Health Tracking</h1>
            <p className="text-gray-600 mt-1">Manage all your health metrics</p>
          </div>
          <button
            onClick={() => onNavigate('historical-data')}
            className="flex items-center gap-2 px-4 py-2 text-dropsugar-green hover:bg-dropsugar-green/10 rounded-xl font-medium transition-colors border border-dropsugar-green/30"
          >
            <History className="w-5 h-5" />
            View History
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <section className="mb-10">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900">Daily Tracking</h2>
            <p className="text-gray-600">Your core glucose, food, step, and activity tools.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {primaryTrackingOptions.map((option) => {
              const Icon = option.icon;
              const colors = getColorClasses(option.color);
              return (
                <button
                  key={option.id}
                  onClick={() => onNavigate(option.id)}
                  className={`group bg-white ${colors.hover} border-2 border-gray-200 ${colors.border} rounded-xl p-6 transition-all text-left shadow-sm hover:shadow-md`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 ${colors.bg} rounded-lg group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-6 h-6 ${colors.icon}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{option.title}</h3>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900">More Health Tools</h2>
            <p className="text-gray-600">Medication, insulin, blood pressure, and weight tracking.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {additionalTrackingOptions.map((option) => {
              const Icon = option.icon;
              const colors = getColorClasses(option.color);
              return (
                <button
                  key={option.id}
                  onClick={() => onNavigate(option.id)}
                  className={`group bg-white ${colors.hover} border-2 border-gray-200 ${colors.border} rounded-xl p-6 transition-all text-left shadow-sm hover:shadow-md`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 ${colors.bg} rounded-lg group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-6 h-6 ${colors.icon}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{option.title}</h3>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
